import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Globe, Plus, Activity, AlertTriangle, Pause, Clock } from 'lucide-react';
import { api } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { ContainerStatusBadge } from '@/components/StatusBadge';
import type { Site, ContainerStatus } from '@/types';

interface StatusCounts {
  total: number;
  RUNNING: number;
  BUILDING: number;
  STOPPED: number;
  ERROR: number;
  PENDING: number;
}

const statusIcons: Record<ContainerStatus, React.ReactNode> = {
  RUNNING: <Activity className="h-5 w-5 text-status-running" />,
  BUILDING: <Clock className="h-5 w-5 text-status-building" />,
  STOPPED: <Pause className="h-5 w-5 text-status-stopped" />,
  ERROR: <AlertTriangle className="h-5 w-5 text-status-error" />,
  PENDING: <Clock className="h-5 w-5 text-status-pending" />,
};

export function DashboardPage() {
  const { user } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const tenantId = user?.tenant?.id;
    if (!tenantId) {
      setLoading(false);
      return;
    }

    api.get<Site[]>(`/sites?tenantId=${tenantId}`)
      .then(setSites)
      .catch(() => { /* handled by error boundary */ })
      .finally(() => setLoading(false));
  }, [user?.tenant?.id]);

  const counts: StatusCounts = {
    total: sites.length,
    RUNNING: sites.filter((s) => s.containerStatus === 'RUNNING').length,
    BUILDING: sites.filter((s) => s.containerStatus === 'BUILDING').length,
    STOPPED: sites.filter((s) => s.containerStatus === 'STOPPED').length,
    ERROR: sites.filter((s) => s.containerStatus === 'ERROR').length,
    PENDING: sites.filter((s) => s.containerStatus === 'PENDING').length,
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  const statCards: { label: string; value: number; status: ContainerStatus }[] = [
    { label: 'Running', value: counts.RUNNING, status: 'RUNNING' },
    { label: 'Building', value: counts.BUILDING, status: 'BUILDING' },
    { label: 'Stopped', value: counts.STOPPED, status: 'STOPPED' },
    { label: 'Errors', value: counts.ERROR, status: 'ERROR' },
  ];

  const recentSites = [...sites]
    .sort((a, b) => {
      const da = a.lastDeployedAt ? new Date(a.lastDeployedAt).getTime() : 0;
      const db = b.lastDeployedAt ? new Date(b.lastDeployedAt).getTime() : 0;
      return db - da;
    })
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-100">Dashboard</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Übersicht über alle Sites
          </p>
        </div>
        <Link
          to="/sites"
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Neue Site
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {/* Total Sites Card */}
        <div className="rounded-xl border border-surface-300 bg-surface-100 p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-600/15">
              <Globe className="h-5 w-5 text-brand-400" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-neutral-100">{counts.total}</p>
              <p className="text-xs text-neutral-500">Gesamt</p>
            </div>
          </div>
        </div>

        {statCards.map(({ label, value, status }) => (
          <div key={status} className="rounded-xl border border-surface-300 bg-surface-100 p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-200">
                {statusIcons[status]}
              </div>
              <div>
                <p className="text-2xl font-semibold text-neutral-100">{value}</p>
                <p className="text-xs text-neutral-500">{label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Deployments */}
      <div className="rounded-xl border border-surface-300 bg-surface-100">
        <div className="border-b border-surface-300 px-5 py-4">
          <h2 className="text-sm font-semibold text-neutral-200">Letzte Aktivität</h2>
        </div>
        {recentSites.length === 0 ? (
          <div className="p-8 text-center text-sm text-neutral-500">
            Noch keine Sites vorhanden.
          </div>
        ) : (
          <div className="divide-y divide-surface-300">
            {recentSites.map((site) => (
              <Link
                key={site.id}
                to={`/sites/${site.id}`}
                className="flex items-center justify-between px-5 py-3 hover:bg-surface-200/50 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-neutral-200">{site.name}</p>
                  <p className="text-xs text-neutral-500">{site.identifier}</p>
                </div>
                <div className="flex items-center gap-3">
                  {site.lastDeployedAt && (
                    <span className="text-xs text-neutral-500">
                      {new Date(site.lastDeployedAt).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  )}
                  <ContainerStatusBadge status={site.containerStatus} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
